import React, { useState, useEffect } from 'react';

export default function PaymentForm({ 
  selectedPlan, 
  selectedPlanData, 
  isAnnual, 
  user, 
  authenticatedFetch, 
  onSuccess, 
  onError,
  isUpgrade = false,
  isDowngrade = false,
  couponCode = null
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Auto-redirect for new subscriptions (not upgrades/downgrades)
  useEffect(() => {
    const createCheckoutSession = async () => {
      // Only auto-redirect for new subscriptions (not upgrades/downgrades)
      if (isUpgrade || isDowngrade) {
        return;
      }

      try {
        setIsLoading(true);
        
        // Create checkout session
        const plan = selectedPlanData;
        const priceId = isAnnual ? plan.annualPriceId : plan.monthlyPriceId;
        const period = isAnnual ? 'annual' : 'monthly';
        // Normalize plan name to lowercase (basic, professional, premium)
        const planName = (selectedPlan || plan.name || '').toLowerCase();

        const requestBody = {
          priceId,
          period,
          planName,
          customerEmail: user.email,
          customerName: user.name || 'Customer'
        };
        
        // Add coupon code if present
        if (couponCode) {
          requestBody.couponCode = couponCode;
        }

        const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/create-subscription`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create checkout session');
        }

        const sessionData = await response.json();
        
        // If this is an upgrade (subscription modified directly), redirect to success page
        if (sessionData.upgraded && sessionData.redirectUrl) {
          window.location.href = sessionData.redirectUrl;
          return;
        }
        
        // For new subscriptions, redirect to Stripe Checkout
        if (!sessionData.url) {
          throw new Error('No checkout URL received from server');
        }
        
        window.location.href = sessionData.url;
        
      } catch (err) {
        console.error('❌ Checkout session creation error:', err);
        setError(err.message || 'Failed to create checkout session');
        setIsLoading(false);
        if (onError) {
          onError(err.message || 'Failed to create checkout session');
        }
      }
    };

    createCheckoutSession();
  }, [selectedPlan, selectedPlanData, isAnnual, user, authenticatedFetch, onError, isUpgrade, isDowngrade, couponCode]);

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Create checkout session or modify subscription
      const plan = selectedPlanData;
      const priceId = isAnnual ? plan.annualPriceId : plan.monthlyPriceId;
      const period = isAnnual ? 'annual' : 'monthly';
      // Normalize plan name to lowercase (basic, professional, premium)
      const planName = (selectedPlan || plan.name || '').toLowerCase();

      const requestBody = {
        priceId,
        period,
        planName,
        customerEmail: user.email,
        customerName: user.name || 'Customer'
      };
      
      // Add coupon code if present
      if (couponCode) {
        requestBody.couponCode = couponCode;
      }

      const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to process subscription change');
      }

      const sessionData = await response.json();
      
      // If this is an upgrade/downgrade (subscription modified directly), redirect to success page
      if (sessionData.upgraded && sessionData.redirectUrl) {
        window.location.href = sessionData.redirectUrl;
        return;
      }
      
      // For new subscriptions, redirect to Stripe Checkout
      if (!sessionData.url) {
        throw new Error('No checkout URL received from server');
      }
      
      window.location.href = sessionData.url;
      
    } catch (err) {
      console.error('❌ Subscription processing error:', err);
      setError(err.message || 'Failed to process subscription change');
      setIsLoading(false);
      if (onError) {
        onError(err.message || 'Failed to process subscription change');
      }
    }
  };

  // For upgrades/downgrades, show confirmation button instead of auto-redirecting
  if (isUpgrade || isDowngrade) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        gap: '1.5rem',
        padding: '2rem'
      }}>
        {error && (
          <div style={{
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            color: '#c33',
            padding: '1rem',
            borderRadius: '6px',
            width: '100%',
            maxWidth: '500px',
            textAlign: 'center'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}
        
        <button
          onClick={handleConfirm}
          disabled={isLoading}
          style={{
            backgroundColor: isLoading ? '#9ca3af' : '#38b44a',
            color: 'white',
            border: 'none',
            padding: '14px 32px',
            borderRadius: '6px',
            fontSize: '18px',
            fontWeight: '600',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s',
            minWidth: '200px'
          }}
          onMouseOver={(e) => {
            if (!isLoading) {
              e.target.style.backgroundColor = '#2d8f3a';
            }
          }}
          onMouseOut={(e) => {
            if (!isLoading) {
              e.target.style.backgroundColor = '#38b44a';
            }
          }}
        >
          {isLoading 
            ? 'Processing...' 
            : isUpgrade 
              ? 'Confirm Upgrade' 
              : 'Confirm Change'
          }
        </button>
        
        {isUpgrade && (
          <p style={{ color: '#666', fontSize: '14px', textAlign: 'center', maxWidth: '500px' }}>
            You will be charged only the prorated difference for the remaining billing period.
          </p>
        )}
        
        {isDowngrade && (
          <p style={{ color: '#666', fontSize: '14px', textAlign: 'center', maxWidth: '500px' }}>
            A credit for the unused portion of your current subscription will be applied to your account.
          </p>
        )}
      </div>
    );
  }


  if (isLoading && !isUpgrade && !isDowngrade) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        fontSize: '16px',
        color: '#666'
      }}>
        Redirecting to secure payment...
      </div>
    );
  }

  if (error && !isUpgrade && !isDowngrade) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        fontSize: '16px',
        color: '#d32f2f',
        backgroundColor: '#ffebee',
        borderRadius: '8px',
        margin: '1rem',
        padding: '1rem'
      }}>
        Payment error: {error}
      </div>
    );
  }

  // This should not be reached for new subscriptions since we redirect
  return null;
}
